import { Columns3 } from 'lucide-react';
import type { Table } from '@tanstack/react-table';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface ColumnVisibilityMenuProps<TRow> {
  table: Table<TRow>;
}

export function ColumnVisibilityMenu<TRow>({ table }: ColumnVisibilityMenuProps<TRow>) {
  const hideable = table.getAllLeafColumns().filter((col) => col.getCanHide());

  if (hideable.length === 0) return null;

  return (
    <DropdownMenu>
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Toggle column visibility"
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-md text-neutral-500',
                  'ring-1 ring-(--color-border) transition-colors duration-150',
                  'hover:bg-neutral-50 hover:text-neutral-800',
                  'focus-visible:shadow-(--shadow-focus-ring-solid) focus-visible:outline-none',
                  'data-[state=open]:bg-neutral-100 data-[state=open]:text-neutral-800',
                )}
              >
                <Columns3 className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Columns</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-label-md tracking-label text-neutral-500 uppercase">
          Columns
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hideable.map((col) => {
          const header = col.columnDef.header;
          const label = typeof header === 'string' ? header : col.id;
          return (
            <DropdownMenuCheckboxItem
              key={col.id}
              checked={col.getIsVisible()}
              onCheckedChange={(v) => {
                col.toggleVisibility(v);
              }}
            >
              {label}
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
